/**
 * Performance monitoring utility for context validation operations
 */

export interface PerformanceMetric {
  operation: string;
  startTime: number;
  endTime: number;
  duration: number;
  success: boolean;
  error?: string;
  metadata?: Record<string, any>;
}

export interface PerformanceBenchmark {
  operation: string;
  averageDuration: number;
  minDuration: number;
  maxDuration: number;
  totalOperations: number;
  successRate: number;
  lastUpdated: Date;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private benchmarks: Map<string, PerformanceBenchmark> = new Map();
  private readonly maxMetricsHistory = 1000;

  /**
   * Start timing an operation
   */
  startTiming(operation: string, metadata?: Record<string, any>): () => PerformanceMetric {
    const startTime = performance.now();
    
    return (success: boolean = true, error?: string) => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const metric: PerformanceMetric = {
        operation,
        startTime,
        endTime,
        duration,
        success,
        error,
        metadata
      };

      this.recordMetric(metric);
      return metric;
    };
  }

  /**
   * Time an async operation
   */
  async timeAsync<T>(
    operation: string,
    asyncFn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<{ result: T; metric: PerformanceMetric }> {
    const endTiming = this.startTiming(operation, metadata);
    
    try {
      const result = await asyncFn();
      const metric = endTiming(true);
      return { result, metric };
    } catch (_error) {
      const metric = endTiming(false, _error instanceof Error ? _error.message : String(_error));
      throw _error;
    }
  }

  /**
   * Record a performance metric
   */
  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    
    // Keep only the last N metrics to prevent memory leaks
    if (this.metrics.length > this.maxMetricsHistory) {
      this.metrics = this.metrics.slice(-this.maxMetricsHistory);
    }

    this.updateBenchmark(metric);
    this.logSlowOperations(metric);
  }

  /**
   * Update benchmark statistics for an operation
   */
  private updateBenchmark(metric: PerformanceMetric): void {
    const existing = this.benchmarks.get(metric.operation);
    
    if (!existing) {
      this.benchmarks.set(metric.operation, {
        operation: metric.operation,
        averageDuration: metric.duration,
        minDuration: metric.duration,
        maxDuration: metric.duration,
        totalOperations: 1,
        successRate: metric.success ? 1 : 0,
        lastUpdated: new Date()
      });
      return;
    }

    const totalOps = existing.totalOperations + 1;
    const successCount = existing.successRate * existing.totalOperations + (metric.success ? 1 : 0);
    
    this.benchmarks.set(metric.operation, {
      operation: metric.operation,
      averageDuration: (existing.averageDuration * existing.totalOperations + metric.duration) / totalOps,
      minDuration: Math.min(existing.minDuration, metric.duration),
      maxDuration: Math.max(existing.maxDuration, metric.duration),
      totalOperations: totalOps,
      successRate: successCount / totalOps,
      lastUpdated: new Date()
    });
  }

  /**
   * Log slow operations for monitoring
   */
  private logSlowOperations(metric: PerformanceMetric): void {
    const slowThresholds = {
      'validate_referent_access': 500, // 500ms
      'set_referent_context': 300,     // 300ms
      'get_referent_form_data': 1000,  // 1000ms
      'context_validation_flow': 2000  // 2000ms
    };

    const threshold = slowThresholds[metric.operation] || 1000;
    
    if (metric.duration > threshold) {
      console.warn(`🐌 Slow operation detected:`, {
        operation: metric.operation,
        duration: `${metric.duration.toFixed(2)}ms`,
        threshold: `${threshold}ms`,
        success: metric.success,
        error: metric.error,
        metadata: metric.metadata
      });
    }
  }

  /**
   * Get performance metrics for an operation
   */
  getMetrics(operation?: string): PerformanceMetric[] {
    if (!operation) return [...this.metrics];
    return this.metrics.filter(m => m.operation === operation);
  }

  /**
   * Get benchmark data for an operation
   */
  getBenchmark(operation: string): PerformanceBenchmark | undefined {
    return this.benchmarks.get(operation);
  }

  /**
   * Get all benchmarks
   */
  getAllBenchmarks(): PerformanceBenchmark[] {
    return Array.from(this.benchmarks.values());
  }

  /**
   * Generate performance report
   */
  generateReport(): string {
    const benchmarks = this.getAllBenchmarks();
    
    if (benchmarks.length === 0) {
      return 'No performance data available';
    }

    let report = '📊 Performance Report\n';
    report += '='.repeat(50) + '\n\n';

    benchmarks.forEach(benchmark => {
      report += `Operation: ${benchmark.operation}\n`;
      report += `  Average Duration: ${benchmark.averageDuration.toFixed(2)}ms\n`;
      report += `  Min/Max Duration: ${benchmark.minDuration.toFixed(2)}ms / ${benchmark.maxDuration.toFixed(2)}ms\n`;
      report += `  Total Operations: ${benchmark.totalOperations}\n`;
      report += `  Success Rate: ${(benchmark.successRate * 100).toFixed(1)}%\n`;
      report += `  Last Updated: ${benchmark.lastUpdated.toISOString()}\n\n`;
    });

    return report;
  }

  /**
   * Clear all metrics and benchmarks
   */
  clear(): void {
    this.metrics = [];
    this.benchmarks.clear();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Export utility functions
export const timeOperation = performanceMonitor.startTiming.bind(performanceMonitor);
export const timeAsync = performanceMonitor.timeAsync.bind(performanceMonitor);